import React, { useRef, useState, useEffect } from 'react';
import { Grid, Card, Typography, Button, Dialog, DialogTitle, DialogContent, RadioGroup, FormControlLabel, DialogActions, Radio, FormControl } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import AuthenticationCountComponent from './authentication-graph/authentication-count.component';
import AuthenticationStatusComponent from './authentication-graph/authentication-status.component';
import AuthenticationFailRateComponent from './authentication-graph/authentication-fail-rate.component';

const Dashboard = (props) => {
    const { totalAuthenticationStatus, authenticationFailRateData, authenticationDateTypeCount, isFetchingAuthenticationFailRate,
        isFetchingAuthenticationStatus, isFetchingAuthenticationCount, breakpoints } = props;

    const [selectedFormat, setSelectedFormat] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [chartTitle, setChartTitle] = useState('');
    const [chartRef, setCurrentChartRef] = useState(null);
    
    const authenticationCountRef = useRef(null);
    const authenticationFailRateRef = useRef(null);
    const authenticationStatusRef = useRef(null);

    const handleFormatChange = (event) => {
        setSelectedFormat(event.target.value);
    };

    const openExportDialog = (title, chartRef) => {
        setChartTitle(title);
        setCurrentChartRef(chartRef);
        setOpenDialog(true);
    };

    const closeExportDialog = () => {
        setOpenDialog(false);
    };

    const handleExport = () => {
        const chartElement = chartRef.current;
    
        if (!chartElement) {
            console.error("Chart element is not available.");
            return;
        }
    
        // Ensure chartElement is a valid DOM node
        if (chartElement instanceof Node) {
            html2canvas(chartElement, { scale: 2, backgroundColor: '#ffffff' })  // Set white background
                .then((canvas) => {
                    // Create a new canvas with title and the chart image
                    const titleCanvas = document.createElement('canvas');
                    const titleContext = titleCanvas.getContext('2d');
    
                    const imgWidth = canvas.width;
                    const imgHeight = canvas.height + 40; // Add space for title (adjust the number 40 for spacing)
    
                    titleCanvas.width = imgWidth;
                    titleCanvas.height = imgHeight;
    
                    // Set the background to white for the whole canvas (title + chart)
                    titleContext.fillStyle = "#ffffff";
                    titleContext.fillRect(0, 0, titleCanvas.width, titleCanvas.height);
    
                    // Draw the title text onto the new canvas
                    titleContext.fillStyle = "#000"; // Black text color
                    titleContext.font = 'bold 18px Arial'; // Title font style
                    titleContext.textAlign = 'center'; // Center-align the text
                    titleContext.textBaseline = 'middle'; // Vertical alignment for text
    
                    // Title position (centered at the top of the canvas)
                    titleContext.fillText(chartTitle, imgWidth / 2, 20); // You can adjust the 20 to change vertical position
    
                    // Draw the chart image (the original chart) below the title
                    titleContext.drawImage(canvas, 0, 40); // Draw the chart image below the title (space for title)
    
                    // Now that the chart and title are on the same canvas, export as PNG or JPEG
                    const imgData = titleCanvas.toDataURL(`image/${selectedFormat}`);
    
                    if (selectedFormat === 'png' || selectedFormat === 'jpeg') {
                        const link = document.createElement('a');
                        link.href = imgData;
                        link.download = `${chartTitle}.${selectedFormat}`;
                        link.click();
                    } else if (selectedFormat === 'pdf') {
                        const pdf = new jsPDF('p', 'mm', 'a4');
                        const pdfWidth = pdf.internal.pageSize.getWidth();
                        const pdfHeight = pdf.internal.pageSize.getHeight();
    
                        const imageProps = pdf.getImageProperties(imgData);
                        const imgWidth = imageProps.width;
                        const imgHeight = imageProps.height;
    
                        const scaleX = pdfWidth / imgWidth;
                        const scaleY = pdfHeight / imgHeight;
                        const scale = Math.min(scaleX, scaleY);
    
                        // Add title to PDF before chart
                        pdf.setFontSize(18);
                        pdf.text(chartTitle, 10, 10);
    
                        pdf.addImage(imgData, 'PNG', 0, 20, imgWidth * scale, imgHeight * scale);
    
                        pdf.save(`${chartTitle}.pdf`);
                    }
                }).catch((error) => {
                    console.error('Error capturing chart as image:', error);
                });
        } else {
            console.error("Chart element is not a valid DOM node.");
        }
    };


    return (
        <Grid container spacing={2}>
            {/* Authentication Count Chart */}
            <Grid item xs={12} sm={12} md={4} lg={4}>
                <Card variant="outlined" sx={{ backgroundColor: '#fff', borderRadius: "8px", padding: '16px' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ color: "#58565f", fontSize: 18, mb: 3, fontWeight: 600 }}>
                                Authentication Count
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ justifyContent: 'flex-end', textAlign: 'end' }}>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                sx={{ fontSize: 12, background: '#3333CFCE', color: '#fff', textTransform: 'none' }}
                                disabled={!isFetchingAuthenticationCount && authenticationDateTypeCount?.length === 0}
                                onClick={() => openExportDialog("Authentication Count", authenticationCountRef)} // Open dialog with specific chart title
                            >
                                Export
                            </Button>
                        </Grid>
                    </Grid>
                    <div ref={authenticationCountRef}>
                        <AuthenticationCountComponent authenticationDateTypeCount={authenticationDateTypeCount} isFetchingAuthenticationCount={isFetchingAuthenticationCount} breakpoints={breakpoints} />
                    </div>
                </Card>
            </Grid>

            {/* Authentication Fail Rate Chart */}
            <Grid item xs={12} sm={12} md={4} lg={4}>
                <Card variant="outlined" sx={{ backgroundColor: '#fff', borderRadius: "8px", padding: '16px' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ color: "#58565f", fontSize: 18, mb: 3, fontWeight: 600 }}>
                                Authentication Fail Rate
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ justifyContent: 'flex-end', textAlign: 'end' }}>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                sx={{ fontSize: 12, background: '#3333CFCE', color: '#fff', textTransform: 'none' }}
                                disabled={!isFetchingAuthenticationFailRate && authenticationFailRateData?.document?.length === 0 || authenticationFailRateData?.product?.length === 0}
                                onClick={() => openExportDialog("Authentication Fail Rate", authenticationFailRateRef)} // Open dialog with specific chart title
                            >
                                Export
                            </Button>
                        </Grid>
                    </Grid>
                    <div ref={authenticationFailRateRef}>
                        <AuthenticationFailRateComponent authenticationFailRateData={authenticationFailRateData} isFetchingAuthenticationFailRate={isFetchingAuthenticationFailRate} breakpoints={breakpoints} />
                    </div>
                </Card>
            </Grid>

            {/* Authentication Status Chart */}
            <Grid item xs={12} sm={12} md={4} lg={4}>
                <Card variant="outlined" sx={{ backgroundColor: '#fff', borderRadius: "8px", padding: '16px' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography sx={{ color: "#58565f", fontSize: 18, mb: 3, fontWeight: 600 }}>
                                Authentication Status
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ justifyContent: 'flex-end', textAlign: 'end' }}>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                sx={{ fontSize: 12, background: '#3333CFCE', color: '#fff', textTransform: 'none' }}
                                disabled={!isFetchingAuthenticationStatus && totalAuthenticationStatus?.length === 0}
                                onClick={() => openExportDialog("Authentication Status", authenticationStatusRef)} // Open dialog with specific chart title
                            >
                                Export
                            </Button>
                        </Grid>
                    </Grid>
                    <div ref={authenticationStatusRef}>
                        <AuthenticationStatusComponent totalAuthenticationStatus={totalAuthenticationStatus} isFetchingAuthenticationStatus={isFetchingAuthenticationStatus} breakpoints={breakpoints} />
                    </div>
                </Card>
            </Grid>

            {/* Export Format Dialog */}
            <Dialog open={openDialog} onClose={closeExportDialog}>
                <DialogTitle>Select Export Format</DialogTitle>
                <DialogContent>
                    <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                            value={selectedFormat}
                            onChange={handleFormatChange}
                        >
                            <FormControlLabel value="png" control={<Radio />} label="PNG" />
                            <FormControlLabel value="jpeg" control={<Radio />} label="JPEG" />
                            <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeExportDialog} variant="outlined"  sx={{ fontSize: 12, background: '#fff', border: '1px solid red', color: 'red', textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        sx={{ fontSize: 12, background: '#3333CFCE', color: '#fff', textTransform: 'none' }}
                        onClick={handleExport}
                    >
                        Export
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default Dashboard;
